import {
  CreatePartyContactRequest,
  PartyContactResponse,
  UpdatePartyContactBody,
} from 'compliance-api/generated';
import { FormikProvider, useFormik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import {
  useCreatePartyContactMutation,
  useUpdatePartyContactMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  SelectButtonOption,
  SelectButtons,
  TextInputField,
} from 'ui';

type PartyContactFormValues = CreatePartyContactRequest &
  UpdatePartyContactBody & {
    id?: number;
  };

interface PartyContactModalProps extends BaseModalProps {
  partyId: number;
  contact?: PartyContactResponse;
}

const authorisedSignatoryOptions: SelectButtonOption<boolean>[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const validationSchema = yup.object<PartyContactFormValues>({
  party_id: yup.number().required(),
  full_name: yup.string().trim().required('Full name is required').max(255),
  email: yup.string().trim().email('Email must be a valid email').max(255),
  phone: yup.string().trim().max(50),
  is_authorised_signatory: yup.boolean(),
});

const getInitialValues = (
  partyId: number,
  contact?: PartyContactResponse,
): PartyContactFormValues => ({
  id: contact?.id,
  party_id: partyId,
  full_name: contact?.full_name || '',
  email: contact?.email || '',
  phone: contact?.phone || '',
  is_authorised_signatory: contact?.is_authorised_signatory || false,
});

const PartyContactModal = ({ partyId, contact, open, onClose }: PartyContactModalProps) => {
  const formik = useFormik<PartyContactFormValues>({
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    enableReinitialize: true,
    initialValues: getInitialValues(partyId, contact),
    onSubmit: async ({ id, party_id, ...values }, { resetForm }) => {
      if (id) {
        await updatePartyContact({ id, data: values });
        resetForm();
        onClose();
        return;
      }

      await createPartyContact({ party_id, ...values });
      resetForm();
      onClose();
    },
  });

  const { mutateAsync: createPartyContact, isLoading: isCreateLoading } =
    useCreatePartyContactMutation(formik.setErrors);
  const { mutateAsync: updatePartyContact, isLoading: isUpdateLoading } =
    useUpdatePartyContactMutation(formik.setErrors);

  const isLoading = isCreateLoading || isUpdateLoading;

  const handleChangeSelect = useCallback(
    (field: keyof PartyContactFormValues) => async (value: unknown) => {
      await formik.setFieldValue(field, value);
      formik.setFieldError(field, '');
    },
    [formik],
  );

  return (
    <ModalContentWrapper
      title={contact ? 'Edit contact' : 'Add new contact'}
      open={open}
      onClose={onClose}
      classes={{ dialogPanel: 'max-w-[502px]' }}
    >
      <FormikProvider value={formik}>
        <div className="space-y-8">
          <TextInputField name="full_name" label="Full name" />

          <TextInputField name="email" label="Email" />

          <TextInputField name="phone" label="Phone number" />

          <SelectButtons
            label="Authorised signatory"
            options={authorisedSignatoryOptions}
            selected={authorisedSignatoryOptions.find(
              ({ value }) => value === formik.values.is_authorised_signatory,
            )}
            onChange={handleChangeSelect('is_authorised_signatory')}
            capitalize={false}
          />

          <Button disabled={isLoading} loading={isLoading} onClick={formik.submitForm}>
            Confirm
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default PartyContactModal;
